import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import InfoIcon from 'icons/info.svg';
import { Trans, useTranslation } from 'react-i18next';
import { accessControlPermissionsToggleReducer } from 'reducers/accessControlPermissionsToggleReducer';

import { useAccountUserAllowanceQuery } from 'components/hooks/accountUserAllowanceQueries';
import {
  useCreateUserMutation,
  useRestoreUserMutation,
  useUpdateUserMutation,
} from 'components/hooks/userMutations';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import ErrorMessage from 'components/application/ErrorMessage';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { buildSelectedAttributes } from 'components/settings/users/helpers/searchImportablePersonnelField';
import useUserForm from 'components/settings/users/hooks/useUserForm';
import SearchImportablePersonnelField from 'components/settings/users/SearchImportablePersonnelField';
import UserDetailsEditor from 'components/settings/users/UserDetailsEditor';
import AccessManagerSection from 'components/settings/users/userForm/components/AccessManagerSection';
import { useUserFormContext } from 'components/settings/users/userForm/contexts/UserFormContext';

import { uniqueValuesInArray } from 'components/helpers/arrays';
import { identityProviderNames } from 'components/helpers/identityProviders';
import { scrollToTop } from 'components/helpers/scrolling';
import {
  accountPermissions,
  doesUserConsumeFullUserAllowance,
  doesUserConsumePersonnelReporteeManagerAllowance,
  isAnyPermissionSelected,
  permissions,
  projectPermissions,
  userParamsFromState,
} from 'components/helpers/users';

export default function UserForm() {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const { divisions, domainUser, samlProviderConfiguration } =
    useUserFormContext();

  const assignablePermissionsPerDivision = useMemo(
    () =>
      Object.fromEntries(
        divisions.map((division) => [
          division.id,
          division.meta.assignablePermissions,
        ]),
      ),
    [divisions],
  );

  const {
    currentUser,
    handleRequestError,
    removeErrorStyling,
    requestError,
    resetAccessControls,
    resetRequestError,
    setCurrentUser,
  } = useUserForm({ assignablePermissionsPerDivision, domainUser });

  const ref = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.userForm.components.UserForm',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation();
  const restoreUserMutation = useRestoreUserMutation();
  const queryClient = useQueryClient();

  const { data: domainAccountUserAllowance } = useAccountUserAllowanceQuery();

  const handlePersonSelection = (option) => {
    resetRequestError();

    setCurrentUser((prevState) => ({
      ...prevState,
      isSelected: true,
      ...buildSelectedAttributes(option, prevState),
    }));
  };

  const handleSelectionReset = (options = { emailOnly: false }) => {
    const resettingAttributes =
      options.emailOnly ?
        { email: '' }
      : { email: '', firstName: '', lastName: '' };

    setCurrentUser((prevState) => ({
      ...prevState,
      isDerived: false,
      isSelected: true,
      ...resettingAttributes,
    }));
  };

  const handleSelectionEdit = () =>
    setCurrentUser((prevState) => ({
      ...prevState,
      isDerived: false,
    }));

  const handleDetailsChange = (changeOptions) => {
    if (requestError.validationErrors[changeOptions.attribute]) {
      removeErrorStyling({ target: { name: changeOptions.attribute } });
    }
    setCurrentUser((prevState) => ({
      ...prevState,
      [changeOptions.attribute]: changeOptions.value,
    }));
  };

  const handleAccessTypeSelection = (accessSelection) =>
    setCurrentUser((prevState) => ({
      ...prevState,
      accessType: accessSelection,
    }));

  const {
    atFullUserCapacity,
    atPersonnelReporteeManagerCapacity,
    fullUserAllowanceConsumptionCount,
    fullUserAllowanceCount,
    personnelReporteeManagerAllowanceConsumptionCount,
    personnelReporteeManagerAllowanceCount,
  } = domainAccountUserAllowance?.data.attributes || {};

  const domainAllowanceConsumption = domainUser && {
    accessControls: domainUser.included.filter(
      ({ type }) => type === 'accessControl',
    ),
    accessType: domainUser.data.attributes.accessType,
    rolesAndCoursesEditor: domainUser.data.attributes.rolesAndCoursesEditor,
  };

  const doesDomainUserConsumeFullUserAllowance =
    !!domainUser &&
    doesUserConsumeFullUserAllowance(domainAllowanceConsumption);

  const isFullUserAllowanceConsumptionDisabled =
    atFullUserCapacity && !doesDomainUserConsumeFullUserAllowance;

  const isPersonnelReporteeManagerAllowanceConsumptionDisabled =
    atPersonnelReporteeManagerCapacity &&
    !doesDomainUserConsumeFullUserAllowance &&
    (!domainUser ||
      !doesUserConsumePersonnelReporteeManagerAllowance({
        ...domainAllowanceConsumption,
        assignableDivisionIds: divisions
          .filter((division) =>
            division.meta.assignablePermissions.includes(
              'personnelReporteeManager',
            ),
          )
          .map(({ id }) => id),
      }));

  const handlePermissionChange = useCallback(
    ({ divisionId, permission }) => {
      setCurrentUser((prevState) => ({
        ...prevState,
        accessControls: accessControlPermissionsToggleReducer(
          prevState.accessControls,
          {
            type: 'TOGGLE_PERMISSION',
            payload: {
              assignablePermissionsPerDivision,
              divisionId,
              permission,
            },
          },
        ),
      }));
    },
    [assignablePermissionsPerDivision, setCurrentUser],
  );

  const handleSelectAllChange = useCallback(
    ({ divisionIds, isSelected, permission }) => {
      setCurrentUser((prevState) => ({
        ...prevState,
        accessControls: accessControlPermissionsToggleReducer(
          prevState.accessControls,
          {
            type: 'TOGGLE_SELECT_ALL',
            payload: {
              assignablePermissionsPerDivision,
              divisionIds,
              isSelected,
              permission,
            },
          },
        ),
      }));
    },
    [assignablePermissionsPerDivision, setCurrentUser],
  );

  const handleSubmission = () => {
    setIsSubmitting(true);

    const userId = computedDomainUser?.data.id || currentUser.personnelUserId;
    const userParams = userParamsFromState({
      currentUser,
      domainUser: computedDomainUser,
    });

    if (['edit', 'upgradePersonnelUser'].includes(computedContext)) {
      updateUserMutation.mutate(
        { id: userId, userParams },
        {
          onSuccess: (data) => {
            window.location.assign(
              `/settings/users?updated_user_id=${data.data.id}`,
            );
          },
          onError: handleSubmissionError,
        },
      );
    } else if (computedContext === 'restore') {
      restoreUserMutation.mutate(
        { id: userId, userParams },
        {
          onSuccess: (data) => {
            window.location.assign(
              `/settings/users?restored_user_id=${data.data.relationships.user.data.id}&personnel_generated=${!!data.meta.personnelGenerated}`,
            );
          },
          onError: handleSubmissionError,
        },
      );
    } else {
      createUserMutation.mutate(
        { userParams },
        {
          onSuccess: (data) => {
            window.location.assign(
              `/settings/users?invited_user_id=${data.data.id}&personnel_generated=${!!data.meta.personnelGenerated}`,
            );
          },
          onError: handleSubmissionError,
        },
      );
    }
  };

  const handleSubmissionError = (e) => {
    setIsSubmitting(false);

    const errorTypes =
      e.response.status === 422 ?
        e.response.data.errors.flatMap(({ types }) => types)
      : [];

    const isFullUserLimitReached = errorTypes.includes('limit_reached');

    const isPersonnelReporteeManagerLimitReached = errorTypes.includes(
      'personnel_reportee_manager_limit_reached',
    );

    if (isFullUserLimitReached || isPersonnelReporteeManagerLimitReached) {
      queryClient.invalidateQueries(['accountUserAllowance']);
      resetAccessControls();

      breadBoard.addInedibleToast({
        fullMessage:
          isPersonnelReporteeManagerLimitReached ?
            i18nTCommon(
              'settings.errors.pleaseUpgradeYourPlanToAddMoreTeamAndLineManagers',
            )
          : assignablePermissions.includes(...projectPermissions) ?
            i18nTCommon(
              'settings.errors.userlimitReachedButYouCanStillAddProjectViewers',
              {
                interpolation: { escapeValue: false },
                singularRAMSName:
                  currentActor.division.attributes.singularRAMSName,
              },
            )
          : i18nTCommon('settings.errors.pleaseUpgradeYourPlanToAddMoreUsers'),
      });
    }

    handleRequestError(e);
    scrollToTop(ref);
  };

  const assignablePermissions = useMemo(() => {
    const divisionPermissions = uniqueValuesInArray(
      divisions.reduce(
        (acc, division) => [...acc, ...division.meta.assignablePermissions],
        [],
      ),
    );

    return permissions
      .concat(accountPermissions)
      .filter((permission) => divisionPermissions.includes(permission));
  }, [accountPermissions, divisions, permissions]);

  const computedDomainUser =
    domainUser || currentUser.restoredDomainUser || null;

  const computedContext =
    currentUser.selectedRestorableUserId ? 'restore'
    : domainUser ? 'edit'
    : currentUser.personnelUserId ? 'upgradePersonnelUser'
    : 'new';

  const isEmailFieldEditable = !!(
    currentUser.email ||
    computedDomainUser?.data.relationships.personnel.data?.id
  );

  const hasAllRequiredAttributes = ['email', 'firstName', 'lastName'].every(
    (attr) => !!currentUser[attr],
  );

  const isMissingAccessSelection =
    currentUser.accessType === 'regular' &&
    !Object.values(currentUser.accessControls).some((accessSelection) =>
      isAnyPermissionSelected({
        accessSelection,
        permissionsToCheck: [...permissions, ...accountPermissions],
      }),
    );

  const disabledSubmitTooltip =
    !hasAllRequiredAttributes ? i18nT('disabledTooltip')
    : isMissingAccessSelection ? i18nT('missingAccessSelectionTooltip')
    : null;

  const headingText = {
    restore: i18nTCommon('shared.restoreUser'),
    new: i18nTCommon('shared.addUser'),
    edit: i18nTCommon('shared.editUser'),
    upgradePersonnelUser: i18nTCommon('shared.addUser'),
  }[computedContext];

  const submitText = {
    restore: i18nTCommon('shared.restoreUser'),
    new: i18nTCommon('shared.addUser'),
    edit: i18nTCommon('shared.saveChanges'),
    upgradePersonnelUser: i18nTCommon('shared.addUser'),
  }[computedContext];

  return (
    <div ref={ref}>
      <h1
        className={classNames(
          'tw-mt-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900',
          computedContext === 'restore' ? 'tw-mb-3' : 'tw-mb-6',
        )}
      >
        {headingText}
      </h1>
      {Object.keys(requestError.validationErrors).length > 0 && (
        <div className='tw-mb-5'>
          <ErrorMessage
            isFallback={requestError.isFallback}
            validationErrors={requestError.validationErrors}
          />
        </div>
      )}
      {computedContext === 'restore' && (
        <p className='tw-mb-5'>{i18nT('restoreUserDescription')}</p>
      )}
      <div className='tw-rounded-none tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-10'>
        {samlProviderConfiguration && computedContext === 'new' && (
          <div className='tw-m-0 tw-mb-8 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
            <div className='tw-mr-3 tw-h-5'>
              <InfoIcon
                className='[&_path]:tw-fill-cyan-800'
                height={20}
                width={20}
              />
            </div>
            <p className='tw-m-0'>
              <Trans
                components={{ span: <span className='tw-font-semibold' /> }}
                i18nKey='authentication.mustBeAddedToIdentityProvider'
                ns='common'
                values={{
                  providerName:
                    identityProviderNames[
                      samlProviderConfiguration.attributes.providerName
                    ],
                }}
              />
            </p>
          </div>
        )}
        <h2 className='tw-mb-8 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
          {i18nTCommon('shared.userDetails')}
        </h2>
        <div className='tw-mb-10 tw-w-90'>
          {!isEmailFieldEditable && (
            <div
              className={classNames(
                'form-group',
                requestError.validationErrors.email &&
                  requestError.validationErrors.email.fieldHighlighted &&
                  'field__invalid',
              )}
            >
              <SearchImportablePersonnelField
                inclPersonnelWithProfileAccess={!computedDomainUser}
                onInputChange={(val) => {
                  if (val === computedDomainUser?.data.attributes.email) {
                    handlePersonSelection({
                      isNotDerived: true,
                      value: val,
                    });
                  }
                }}
                onMenuOpen={() => {
                  removeErrorStyling({ target: { name: 'email' } });
                }}
                onSelection={handlePersonSelection}
              />
            </div>
          )}
          {currentUser.isSelected && (
            <UserDetailsEditor
              currentUser={currentUser}
              formContext={computedContext}
              isEmailFieldEditable={isEmailFieldEditable}
              onDetailsChange={handleDetailsChange}
              onSelectionEdit={handleSelectionEdit}
              onSelectionReset={handleSelectionReset}
              removeErrorStyling={removeErrorStyling}
              requestError={requestError}
            />
          )}
        </div>
        {currentUser.isSelected && (
          <>
            <hr className='tw-m-0 tw-h-px tw-border-0 tw-bg-grey-100' />
            <AccessManagerSection
              assignablePermissions={assignablePermissions}
              currentUser={currentUser}
              fullUserAllowanceConsumptionCount={
                fullUserAllowanceConsumptionCount
              }
              fullUserAllowanceCount={fullUserAllowanceCount}
              isAtFullUserCapacity={atFullUserCapacity}
              isAtPersonnelReporteeManagerCapacity={
                atPersonnelReporteeManagerCapacity
              }
              isEditingSelf={
                currentActor.user.id === computedDomainUser?.data.id
              }
              isFullUserAllowanceConsumptionDisabled={
                isFullUserAllowanceConsumptionDisabled
              }
              isPersonnelReporteeManagerAllowanceConsumptionDisabled={
                isPersonnelReporteeManagerAllowanceConsumptionDisabled
              }
              onAccessTypeSelection={handleAccessTypeSelection}
              onPermissionChange={handlePermissionChange}
              onSelectAllChange={handleSelectAllChange}
              personnelReporteeManagerAllowanceConsumptionCount={
                personnelReporteeManagerAllowanceConsumptionCount
              }
              personnelReporteeManagerAllowanceCount={
                personnelReporteeManagerAllowanceCount
              }
            />
          </>
        )}
      </div>
      <div className='tw-flex tw-h-18 tw-items-center tw-justify-end tw-rounded-none tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-10 tw-py-4'>
        <TextButton
          className='tw-mr-3'
          color='blue'
          href='/settings/users'
          size='md'
        >
          {i18nTCommon('shared.cancel')}
        </TextButton>
        <ConditionalWrapper
          condition={!!disabledSubmitTooltip}
          wrapper={(children) => (
            <Tooltip
              placement='top'
              tooltip={disabledSubmitTooltip}
              trigger='hover'
            >
              {children}
            </Tooltip>
          )}
        >
          <FilledButton
            color='mint'
            disabled={!!disabledSubmitTooltip || isSubmitting}
            onClick={handleSubmission}
          >
            {submitText}
          </FilledButton>
        </ConditionalWrapper>
      </div>
    </div>
  );
}
